import React from 'react';
import { useDashboard } from '../../hooks/useAdmin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Users, 
  DollarSign, 
  AlertTriangle, 
  TrendingUp, 
  CheckCircle,
  Trophy,
  CreditCard
} from 'lucide-react';
import { ShimmerMetricCard } from '../../components/animations/ShimmerLoader';
import ProgressRing from '../../components/animations/ProgressRing';
import RippleButton from '../../components/animations/RippleButton';
import MorphCard from '../../components/animations/MorphCard';
import { FadeInUp, StaggeredList } from '../../components/animations/PageTransition';

const AdminDashboard = () => {
  const { metrics, loading, error, fetchMetrics } = useDashboard();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6 animate-shimmer bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <ShimmerMetricCard key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <ShimmerMetricCard key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          </motion.div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <RippleButton
            onClick={fetchMetrics}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </RippleButton>
        </div>
      </motion.div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const metricsCards = [
    {
      title: 'Active Contests',
      value: metrics?.active_contests || 0,
      description: 'Currently running contests',
      icon: Trophy,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Total Participants',
      value: metrics?.total_participants || 0,
      description: 'Across all contests',
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(metrics?.total_revenue || 0),
      description: 'From successful payments',
      icon: DollarSign,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Today\'s Revenue',
      value: formatCurrency(metrics?.todays_revenue || 0),
      description: 'Revenue generated today',
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  const additionalMetrics = [
    {
      title: 'Payment Success Rate',
      value: formatPercentage(metrics?.payment_success_rate || 0),
      description: 'Successful payment ratio',
      icon: CheckCircle,
      color: metrics?.payment_success_rate >= 90 ? 'text-green-600' : 'text-yellow-600',
      bgColor: metrics?.payment_success_rate >= 90 ? 'bg-green-100' : 'bg-yellow-100'
    },
    {
      title: 'Pending Refunds',
      value: formatCurrency(metrics?.pending_refunds || 0),
      description: 'Amount pending for refund',
      icon: CreditCard,
      color: metrics?.pending_refunds > 0 ? 'text-red-600' : 'text-gray-600',
      bgColor: metrics?.pending_refunds > 0 ? 'bg-red-100' : 'bg-gray-100'
    }
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <FadeInUp delay={0.1}>
        <div className="flex items-center justify-between">
          <div>
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl font-bold text-gray-900"
            >
              Dashboard
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-gray-600"
            >
              Overview of your contest platform
            </motion.p>
          </div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center space-x-2"
          >
            <motion.div
              animate={{ 
                boxShadow: [
                  '0 0 0 0 rgba(34, 197, 94, 0.7)',
                  '0 0 0 4px rgba(34, 197, 94, 0)',
                  '0 0 0 0 rgba(34, 197, 94, 0.7)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="rounded-full"
            >
              <Badge variant="outline" className="text-green-600 border-green-600">
                <Activity className="w-3 h-3 mr-1" />
                Live
              </Badge>
            </motion.div>
          </motion.div>
        </div>
      </FadeInUp>

      {/* Main Metrics */}
      <StaggeredList className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" stagger={0.1}>
        {metricsCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <MorphCard key={index} hoverScale={1.03} glowColor="rgba(59, 130, 246, 0.1)">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {metric.title}
                </CardTitle>
                <motion.div 
                  className={`p-2 rounded-full ${metric.bgColor}`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <Icon className={`h-4 w-4 ${metric.color}`} />
                </motion.div>
              </CardHeader>
              <CardContent>
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-2xl font-bold text-gray-900"
                >
                  {metric.value}
                </motion.div>
                <p className="text-xs text-gray-500 mt-1">{metric.description}</p>
              </CardContent>
            </MorphCard>
          );
        })}
      </StaggeredList>

      {/* Additional Metrics */}
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.2
            }
          }
        }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {additionalMetrics.map((metric, index) => {
          const Icon = metric.icon;
          const percentage = metric.title === 'Payment Success Rate' ? parseFloat(metric.value) : 0;
          
          return (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
            >
              <MorphCard hoverScale={1.02}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {metric.title}
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    {metric.title === 'Payment Success Rate' && (
                      <ProgressRing 
                        percentage={percentage} 
                        size={40} 
                        strokeWidth={4}
                        color="text-green-600"
                      >
                        <span className="text-xs font-bold">{percentage}%</span>
                      </ProgressRing>
                    )}
                    <motion.div 
                      className={`p-2 rounded-full ${metric.bgColor}`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <Icon className={`h-4 w-4 ${metric.color}`} />
                    </motion.div>
                  </div>
                </CardHeader>
                <CardContent>
                  <motion.div 
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-2xl font-bold text-gray-900"
                  >
                    {metric.value}
                  </motion.div>
                  <p className="text-xs text-gray-500 mt-1">{metric.description}</p>
                </CardContent>
              </MorphCard>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Quick Actions */}
      <FadeInUp delay={0.6}>
        <MorphCard>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <StaggeredList className="grid grid-cols-1 md:grid-cols-3 gap-4" stagger={0.1}>
              <motion.button 
                whileHover={{ 
                  scale: 1.02, 
                  backgroundColor: '#f9fafb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                whileTap={{ scale: 0.98 }}
                className="p-4 text-left border rounded-lg transition-all duration-200"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Trophy className="h-8 w-8 text-blue-600 mb-2" />
                </motion.div>
                <h3 className="font-medium text-gray-900">Create Contest</h3>
                <p className="text-sm text-gray-600">Set up a new quiz contest</p>
              </motion.button>
              
              <motion.button 
                whileHover={{ 
                  scale: 1.02, 
                  backgroundColor: '#f9fafb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                whileTap={{ scale: 0.98 }}
                className="p-4 text-left border rounded-lg transition-all duration-200"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <CreditCard className="h-8 w-8 text-green-600 mb-2" />
                </motion.div>
                <h3 className="font-medium text-gray-900">Process Refunds</h3>
                <p className="text-sm text-gray-600">Handle pending refund requests</p>
              </motion.button>
              
              <motion.button 
                whileHover={{ 
                  scale: 1.02, 
                  backgroundColor: '#f9fafb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                whileTap={{ scale: 0.98 }}
                className="p-4 text-left border rounded-lg transition-all duration-200"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Users className="h-8 w-8 text-purple-600 mb-2" />
                </motion.div>
                <h3 className="font-medium text-gray-900">Manage Winners</h3>
                <p className="text-sm text-gray-600">Review and verify winners</p>
              </motion.button>
            </StaggeredList>
          </CardContent>
        </MorphCard>
      </FadeInUp>

      {/* System Status */}
      <FadeInUp delay={0.8}>
        <MorphCard>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">System Status</CardTitle>
            <CardDescription>Current system health</CardDescription>
          </CardHeader>
          <CardContent>
            <StaggeredList className="space-y-3" stagger={0.1}>
              <motion.div 
                className="flex items-center justify-between"
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <span className="text-sm text-gray-600">Database Connection</span>
                <motion.div
                  animate={{ 
                    boxShadow: [
                      '0 0 0 0 rgba(34, 197, 94, 0.4)',
                      '0 0 0 4px rgba(34, 197, 94, 0)',
                      '0 0 0 0 rgba(34, 197, 94, 0.4)'
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="rounded-full"
                >
                  <Badge className="bg-green-100 text-green-800">Healthy</Badge>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="flex items-center justify-between"
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <span className="text-sm text-gray-600">Payment Gateway</span>
                <motion.div
                  animate={{ 
                    boxShadow: [
                      '0 0 0 0 rgba(34, 197, 94, 0.4)',
                      '0 0 0 4px rgba(34, 197, 94, 0)',
                      '0 0 0 0 rgba(34, 197, 94, 0.4)'
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  className="rounded-full"
                >
                  <Badge className="bg-green-100 text-green-800">Connected</Badge>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="flex items-center justify-between"
                whileHover={{ x: 4 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <span className="text-sm text-gray-600">Last Backup</span>
                <motion.span 
                  animate={{ opacity: [1, 0.7, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="text-sm text-gray-500"
                >
                  2 hours ago
                </motion.span>
              </motion.div>
            </StaggeredList>
          </CardContent>
        </MorphCard>
      </FadeInUp>
    </motion.div>
  );
};

export default AdminDashboard;